import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, take } from "rxjs";

import { environment } from "@environments/environment";
import { Employee } from "@shared/models/employee.interface";
import { MilestoneCardData } from "@shared/models/milestone-cards.interface";
import { NetPaymentReport, NetPaymentReportPagination } from "@shared/models/net-payments.interface";

@Injectable({
	providedIn: "root"
})
export class NetPaymentOrdersService {
	constructor(private http: HttpClient) {}

	getNetPaymentKpiCardData(milestoneId: string): Observable<MilestoneCardData[]> {
		return this.http.get<any>(`${environment.apiUri}/v1/payment-orders/${milestoneId}/kpi-card-count/report`).pipe(
			map(card => {
				let milestoneCard: MilestoneCardData[] = [];
				const isHandshakeSuccessful =
					(card.totalWpayAmount !== null || card.totalPayrollAmount !== null) &&
					card.totalWpayAmount?.value === card.totalPayrollAmount?.value;

				if (isHandshakeSuccessful) {
					milestoneCard = [
						{
							key: "totalCount",
							value: card?.headcount
						},
						{
							key: "totalAmount",
							value: card.totalWpayAmount?.value !== undefined ? card.totalWpayAmount.value : null,
							currencyCode:
								card.totalWpayAmount?.currency !== undefined ? card.totalWpayAmount.currency : "USD"
						},
						{
							key: "totalDeductionAmount",
							value:
								card.totalDeductionAmount?.value !== undefined
									? card.totalDeductionAmount?.value
									: null,
							currencyCode:
								card.totalDeductionAmount?.currency !== undefined
									? card.totalDeductionAmount.currency
									: "USD"
						}
					];
				} else {
					milestoneCard = [
						{
							key: "totalCount",
							value: card?.headcount
						},
						{
							key: "totalAmount",
							value: card.totalWpayAmount?.value !== undefined ? card.totalWpayAmount.value : null,
							currencyCode:
								card.totalWpayAmount?.currency !== undefined ? card.totalWpayAmount.currency : "USD"
						},
						{
							key: "totalPayroll",
							value: card.totalPayrollAmount?.value !== undefined ? card.totalPayrollAmount?.value : null,
							currencyCode:
								card.totalPayrollAmount?.currency !== undefined
									? card.totalPayrollAmount.currency
									: "USD"
						},
						{
							key: "totalDeductionAmount",
							value:
								card.totalDeductionAmount?.value !== undefined
									? card.totalDeductionAmount?.value
									: null,
							currencyCode:
								card.totalDeductionAmount?.currency !== undefined
									? card.totalDeductionAmount.currency
									: "USD"
						}
					];
				}

				return milestoneCard;
			}),
			take(1)
		);
	}

	getNetPaymentOrders(
		milestoneId: string,
		pageNumber: number,
		pageSize: number,
		employeeQueryString: string
	): Observable<NetPaymentReportPagination> {
		const urlModifier = employeeQueryString ? `&employeeQueryString=${employeeQueryString}` : "";

		return this.http
			.get<NetPaymentReportPagination>(
				`${environment.apiUri}/v1/payment-orders/${milestoneId}/report?pageNumber=${pageNumber}&pageSize=${pageSize}${urlModifier}`
			)
			.pipe(
				take(1),
				map(response => ({
					totalCount: response.totalCount,
					totalPages: response.totalPages,
					pageNumber: response.pageNumber,
					items: response.items.map(
						(item: any): NetPaymentReport => ({
							employeeId: item.employee.id,
							employeeExternalId: item.employee.externalId,
							employeeName: item.employee.firstName,
							surname: item.employee.lastName,
							source: item.source,
							amount: {
								value: item.amount.value,
								currency: item.amount.currency
							},
							deductionAmount: {
								value: item.deductionAmount?.value,
								currency: item.deductionAmount?.currency
							}
						})
					)
				}))
			);
	}

	getEmployeeAutocomplete(query: string, id: string): Observable<Employee[]> {
		return this.http
			.get<any>(`${environment.apiUri}/v1/employees?payGroupIds=${id}&queryString=${query}`)
			.pipe(map(data => data.items));
	}
}
